import { Button, Checkbox, Input, Radio, RadioChangeEvent, notification } from 'antd';
import { _FORM_ITEM_LAYOUT, _TAIL_FORM_ITEM_LAYOUT } from '../../../constants';
import { BloodlineDiseasesWrapper, FormItemsWrapper, StyledForm } from './style';
import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { AddMoreBtnContainer } from '../../Common/Button/style';
import { useUpdateProfessionalClientBloodlineDiseases } from '../../../hooks/professionals/useClientGeneralInfo';
import { useUpdateClientBloodlineDiseases } from '../../../hooks/client/useClientGeneralInfo';
import { useGetProfessionalClient } from '../../../hooks/professionals/useProfessionalClient';
import { NotificationTitle } from '../../Common/Notification/style';
import { CheckCircleOutlined } from '@ant-design/icons';
import { theme } from '../../../styles/theme';

const familyMembersOptions = [
  { id: '1', name: 'mother' },
  { id: '2', name: 'father' },
  { id: '3', name: 'brother' },
  { id: '4', name: 'sister' },
  { id: '5', name: `grandfather (father's side)` },
  { id: '6', name: `grandfather (maternal side)` },
  { id: '7', name: `grandmother (father's side)` },
  { id: '8', name: `grandmother (maternal side)` },
  { id: '9', name: `uncle / aunt (father's side)` },
  { id: '10', name: `uncle / aunt (maternal side)` },
];

const BloodlineDiseasesForm = () => {
  const [api, contextHolder] = notification.useNotification();
  const [form] = StyledForm.useForm();
  const { selectedClient } = useSelector((state: RootState) => state.professionals);
  const { user } = useSelector((state: RootState) => state.auth);
  const isClient = !!user && 'professionalCode' in user;

  const { update: updateProfessional, loading: loadingProfessional } = useUpdateProfessionalClientBloodlineDiseases();
  const { update: updateClient, loading: loadingClient } = useUpdateClientBloodlineDiseases();
  const { refetch: refetchClient } = useGetProfessionalClient(selectedClient?.id as string);

  const [withBloodlineDiseases, setWithBloodlineDiseases] = useState<boolean>(Boolean(selectedClient?.bloodlineDiseases?.length) ?? false);

  // Track selected family members per disease index
  const [selectedFamilyMembers, setSelectedFamilyMembers] = useState<Record<number, Record<string, boolean>>>({});

  useEffect(() => {
    if (selectedClient?.bloodlineDiseases) {
      const initialDiseases = selectedClient.bloodlineDiseases.map((d: any) => ({
        id: d.id,
        whichDisease: d.disease,
        familyMembers: d.familyMembers?.reduce((acc: any, fm: any) => {
          const key = fm.family_member_key || fm.familyMemberKey;
          acc[key] = {
            death: {
              age: fm.death?.age,
              cause: fm.death?.cause,
            }
          };
          return acc;
        }, {})
      }));

      form.setFieldsValue({ bloodlineDiseases: initialDiseases });

      const initialSelections: Record<number, Record<string, boolean>> = {};
      selectedClient.bloodlineDiseases.forEach((d: any, index: number) => {
        initialSelections[index] = {};
        d.familyMembers?.forEach((fm: any) => {
          const key = fm.family_member_key || fm.familyMemberKey;
          const option = familyMembersOptions.find(opt => opt.name === key);
          if (option) {
            initialSelections[index][option.id] = true;
          }
        });
      });
      setSelectedFamilyMembers(initialSelections);
    }
  }, [selectedClient, form]);

  const onFinish = async (values: any) => {
    const diseases = values.bloodlineDiseases || [];
    const validationErrors: string[] = [];

    if (withBloodlineDiseases && diseases.filter((d: any) => !d?._destroy).length === 0) {
      validationErrors.push('Please add at least one disease or select "No".');
    }

    const paramsToSend = diseases
      .map((d: any, originalIndex: number) => {
        if (!d || d._destroy) return null;

        const diseaseName = d.whichDisease || `Disease ${originalIndex + 1}`;

        if (!d.whichDisease) {
          validationErrors.push(`Disease name is missing for entry ${originalIndex + 1}.`);
        }

        const familyMembers: any[] = [];
        let hasMember = false;

        familyMembersOptions.forEach(opt => {
          if (selectedFamilyMembers[originalIndex]?.[opt.id]) {
            hasMember = true;
            const fmData = d.familyMembers?.[opt.name];
            const ageInput = fmData?.death?.age;

            if (ageInput === undefined || ageInput === null || ageInput === '') {
              validationErrors.push(`${diseaseName}: Age is missing for ${opt.name}.`);
            }

            const parsedAge = parseInt(ageInput, 10);

            familyMembers.push({
              familyMemberKey: opt.name,
              death: {
                age: isNaN(parsedAge) ? 0 : parsedAge,
                cause: fmData?.death?.cause || '',
              }
            });
          }
        });

        if (!hasMember) {
          validationErrors.push(`${diseaseName}: At least one family member must be selected.`);
        }

        const idValue = (d.id != null && d.id !== '' && String(d.id) !== '0') ? String(d.id) : '';

        return {
          id: idValue,
          disease: d.whichDisease,
          familyMembers: familyMembers,
        };
      })
      .filter((item: any) => item !== null);

    if (validationErrors.length > 0) {
      api.warning({
        message: <NotificationTitle>Missing Information</NotificationTitle>,
        description: (
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {validationErrors.map((err, i) => (
              <div key={i} style={{ marginBottom: '4px' }}>• {err}</div>
            ))}
          </div>
        ),
        placement: 'topRight',
        style: { background: `${theme.bg.secondary}`, borderRadius: 10 },
      });
      return;
    }

    try {
      let res;
      if (isClient) {
        res = await updateClient(paramsToSend);
      } else {
        res = await updateProfessional(selectedClient?.id as string, paramsToSend);
      }

      const success = isClient ? res?.clientUpdateBloodlineDiseases : res?.professionalClientUpdateBloodlineDiseases;

      if (success) {
        await refetchClient();
        api.info({
          message: <NotificationTitle>Bloodline diseases updated successfully</NotificationTitle>,
          placement: 'topRight',
          icon: <CheckCircleOutlined style={{ color: theme.color.primary }} />,
          style: { background: `${theme.bg.secondary}`, borderRadius: 10, alignItems: 'center', display: 'flex' },
        });
      }
    } catch (error: any) {
      console.error('Error saving bloodline diseases:', error);

      let description = 'There was an issue saving the details. Please try again.';
      if (error.graphQLErrors?.length) {
        description = error.graphQLErrors.map((e: any) => e.message).join(', ');
      } else if (error.message) {
        description = error.message;
      }

      api.error({
        message: <NotificationTitle>Error saving bloodline diseases</NotificationTitle>,
        description: description,
        placement: 'topRight',
        style: { background: `${theme.bg.secondary}`, borderRadius: 10, alignItems: 'center', display: 'flex' },
      });
    }
  };

  const onChangeWithBloodlineDiseases = ({ target: { value } }: RadioChangeEvent) => {
    setWithBloodlineDiseases(value);
  };

  const onRemoveDisease = (fieldIndex: any) => {
    const oldBloodlineDisease = form.getFieldValue('bloodlineDiseases') || [];
    const newBloodlineDisease = oldBloodlineDisease.map((item: any, index: number) =>
      index === fieldIndex ? { ...item, _destroy: true } : item
    );
    form.setFieldsValue({ bloodlineDiseases: newBloodlineDisease });
  };

  const handleFamilyMemberSelection = (diseaseIndex: number, familyMemberId: string, checked: boolean) => {
    setSelectedFamilyMembers((prev) => ({
      ...prev,
      [diseaseIndex]: {
        ...prev[diseaseIndex],
        [familyMemberId]: checked,
      },
    }));
  };

  return (
    <>
      {contextHolder}
      <StyledForm
        {..._FORM_ITEM_LAYOUT}
        form={form}
        name="bloodline_diseases_form"
        onFinish={onFinish}
        initialValues={{}}
        style={{ marginTop: '1rem' }}
        scrollToFirstError
      >
        <p>In my family bloodline there are specific diseases</p>
        <Radio.Group
          options={[
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ]}
          onChange={onChangeWithBloodlineDiseases}
          value={withBloodlineDiseases}
          optionType="button"
          buttonStyle="solid"
        />
        {withBloodlineDiseases && (
          <FormItemsWrapper>
            <StyledForm.List name="bloodlineDiseases">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => {
                    const diseaseData = form.getFieldValue(['bloodlineDiseases', field.name]);
                    if (diseaseData?._destroy) return null;

                    return (
                      <BloodlineDiseasesWrapper key={field.key}>
                        <div className="number-container">
                          <p>{`${index + 1})`}</p>
                          <Button
                            size="small"
                            onClick={() => onRemoveDisease(field.name)}
                          >
                            Remove
                          </Button>
                        </div>
                        <StyledForm.Item
                          name={[field.name, 'id']}
                          hidden
                        >
                          <Input />
                        </StyledForm.Item>
                        <StyledForm.Item
                          name={[field.name, 'whichDisease']}
                          label="Which disease?"
                          labelAlign="left"
                          rules={[{ required: true, message: 'This is a required field!' }]}
                        >
                          <Input />
                        </StyledForm.Item>
                        <label style={{ display: 'block', marginBottom: '8px' }}>At which family member?</label>
                        {familyMembersOptions.map((familyMember) => (
                          <div key={familyMember.id} style={{ marginBottom: '1rem' }}>
                            <Checkbox
                              checked={!!selectedFamilyMembers[field.name]?.[familyMember.id]}
                              onChange={(e) => handleFamilyMemberSelection(field.name, familyMember.id, e.target.checked)}
                              style={{ marginBottom: '0.5rem' }}
                            >
                              {familyMember.name}
                            </Checkbox>
                            {selectedFamilyMembers[field.name]?.[familyMember.id] && (
                              <div style={{ paddingLeft: '24px' }}>
                                <StyledForm.Item
                                  name={[field.name, "familyMembers", familyMember.name, "death", "age"]}
                                  label="At which age?"
                                  rules={[{ required: true, message: 'Please specify at which age' }]}
                                >
                                  <Input placeholder="e.g. 75" />
                                </StyledForm.Item>
                                <StyledForm.Item
                                  name={[field.name, "familyMembers", familyMember.name, "death", "cause"]}
                                  label="What was the cause?"
                                >
                                  <Input placeholder="e.g. Heart attack" />
                                </StyledForm.Item>
                              </div>
                            )}
                          </div>
                        ))}
                      </BloodlineDiseasesWrapper>
                    );
                  })}
                  <AddMoreBtnContainer>
                    <Button
                      size="small"
                      onClick={() => add()}
                    >
                      Add more
                    </Button>
                  </AddMoreBtnContainer>
                </>
              )}
            </StyledForm.List>
          </FormItemsWrapper>
        )}
        <StyledForm.Item {..._TAIL_FORM_ITEM_LAYOUT}>
          <Button
            type="primary"
            htmlType="submit"
            loading={isClient ? loadingClient : loadingProfessional}
          >
            Save
          </Button>
        </StyledForm.Item>
      </StyledForm>
    </>
  );
};

export default BloodlineDiseasesForm;
